import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";

/*
 * BCM-891 — the Product Type Configurations table shown above the batch
 * allocation PDF fields, on both Add New CMO and Update CMO.
 *
 * One definition for both screens: the two had drifted into near-identical
 * copies, so a change to the wording or the columns had to be made twice.
 *
 * Rows are memoised. The change handler replaces only the edited row's object
 * and leaves the others' identities alone, so editing one row re-renders that
 * row's three dropdowns rather than every row's — which is what made the Yes/No
 * dropdowns feel slow on a CMO with several product types. Give it a stable
 * onChange (useCallback) or the memo cannot do its job.
 */

/** The PDF title is derived from Artwork Code Required — never entered. */
export const allocationPdfTitle = (artworkRequired) =>
  artworkRequired === true ? "Allocation and Artwork Memo" : "Allocation Memo";

export const PDF_CONFIG_FIELDS = [
  ["documentIDReqd", "Document ID Required"],
  ["artworkRequired", "Artwork Code Required"],
  ["allocationApprovalReqd", "Approval Required"],
];

const toYesNo = (v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : "No");

/*
 * The rows actually shown, normalised so Add, View and Update present the same
 * table whichever screen built the list.
 *
 * Add and View render this from productTypesData, which starts from the CMO's
 * saved configuration and merges the product-type master into it. Update
 * renders it from the master with the saved values merged on. Those two arrive
 * in different orders, and the first can contain entries for product types that
 * are no longer in the master — a saved entry carries no productTypeName, so
 * those rendered as a nameless row with meaningless dropdowns.
 *
 * Dropping the nameless rows loses nothing: the save paths map over the full
 * arrays, not over what this table displays, so a stale entry is still sent
 * back exactly as it was stored.
 */
// Product names in ascending order, the same on Add, View and Update.
const visibleRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((r) => String(r?.productTypeName || "").trim() !== "")
    .sort((a, b) =>
      String(a.productTypeName).localeCompare(String(b.productTypeName), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );

const ConfigRow = React.memo(function ConfigRow({
  type,
  disabled,
  onChange,
  cellClassName,
}) {
  return (
    <TableRow>
      <TableCell className={cellClassName} style={{ fontSize: 16 }}>
        {type.productTypeName}
      </TableCell>
      {PDF_CONFIG_FIELDS.map(([field]) => (
        <TableCell key={field} className={cellClassName} style={{ fontSize: 16 }}>
          <FormControl variant="outlined" size="small">
            <Select
              value={toYesNo(type[field])}
              onChange={(e) =>
                onChange(type.productTypeID, field, e.target.value === "Yes")
              }
              disabled={disabled}
              displayEmpty
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </TableCell>
      ))}
      <TableCell className={cellClassName} style={{ fontSize: 16 }}>
        {allocationPdfTitle(type.artworkRequired)}
      </TableCell>
    </TableRow>
  );
});

const ProductTypePdfConfigTable = ({
  rows = [],
  disabled = false,
  onChange,
  headClassName,
  headCellClassName,
  bodyCellClassName,
  tableContainerClassName,
}) => (
  <div style={{ marginBottom: 32 }}>
    <Typography
      style={{
        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        fontWeight: 800,
        fontSize: 18,
        marginBottom: 8,
      }}
    >
      Product Type Configurations
    </Typography>
    <TableContainer component={Paper} className={tableContainerClassName}>
      <Table>
        <TableHead className={headClassName}>
          <TableRow>
            <TableCell className={headCellClassName} style={{ fontWeight: 700 }}>
              Product Type
            </TableCell>
            {PDF_CONFIG_FIELDS.map(([field, label]) => (
              <TableCell key={field} className={headCellClassName} style={{ fontWeight: 700 }}>
                {label}
              </TableCell>
            ))}
            <TableCell className={headCellClassName} style={{ fontWeight: 700 }}>
              Title
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows(rows).map((type) => (
            <ConfigRow
              key={type.productTypeID}
              type={type}
              disabled={disabled}
              onChange={onChange}
              cellClassName={bodyCellClassName}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </div>
);

export default ProductTypePdfConfigTable;
